import React, { useState, useEffect, useMemo } from 'react';
import { TennisLiveApi, type Fixture } from '../../services/TennisLiveApi';
import { ShimmerLoading } from './ShimmerLoading';
import { MatchTile } from './MatchTile';
import { ArrowLeft, ChevronDown, Check } from 'lucide-react';
import { DateCarousel } from './DateCarousel';
import '../Rankings/RankingsView.css';

interface TournamentDetailsViewProps {
  tournament: {
    name: string;
    tournament_key: string;
    events: any[];
  };
  onClose: () => void;
}

export const TournamentDetailsView: React.FC<TournamentDetailsViewProps> = ({ tournament, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [fixtures, setFixtures] = useState<Fixture[]>([]);
  
  const [availableYears, setAvailableYears] = useState<string[]>([]);
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>(() => new Date().toISOString().split('T')[0]);
  const [isLiveFilter, setIsLiveFilter] = useState(false);

  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);

  useEffect(() => {
    const fetchFixtures = async () => {
      setLoading(true);
      const data = await TennisLiveApi.getTournamentFixtures(tournament.tournament_key);
      const validData = data || [];
      setFixtures(validData);
      
      const yearsSet = new Set<string>();
      const catsSet = new Set<string>();
      
      validData.forEach(f => {
        if (f.season) yearsSet.add(f.season);
        if (f.matchCategory) catsSet.add(f.matchCategory);
      });
      
      const years = Array.from(yearsSet).sort((a, b) => b.localeCompare(a));
      const cats = Array.from(catsSet).sort();
      
      setAvailableYears(years);
      setAvailableCategories(cats);
      
      if (years.length > 0) setSelectedYear(years[0]);
      if (cats.length > 0) setSelectedCategory(cats[0]);
      
      // We don't override selectedDate here so it defaults to today
      
      setLoading(false);
    };
    fetchFixtures();
  }, [tournament.tournament_key]);


  useEffect(() => {
    if (selectedYear && selectedCategory && fixtures.length > 0) {
      // Just ensure we don't reset selectedDate, let it stay on today (initial state)
    }
  }, [selectedYear, selectedCategory, fixtures]);

  const filteredFixtures = useMemo(() => {
    return fixtures.filter(f => {
      const matchYear = f.season === selectedYear;
      const matchCat = f.matchCategory === selectedCategory;
      const matchDateStr = f.date ? new Date(f.date).toISOString().split('T')[0] : '';
      const matchDate = matchDateStr === selectedDate;
      
      if (isLiveFilter) {
        const safeStatus = String(f.status || '');
        if (!safeStatus.includes('🔴') && !safeStatus.toLowerCase().includes('set')) return false;
      }
      
      return matchYear && matchCat && matchDate;
    });
  }, [fixtures, selectedYear, selectedCategory, selectedDate, isLiveFilter]);

  const groupedFixtures = useMemo(() => {
    const grouped: Record<string, Fixture[]> = {};
    filteredFixtures.forEach(f => {
      const round = String(f.round || 'Outros').toUpperCase();
      if (!grouped[round]) grouped[round] = [];
      grouped[round].push(f);
    });
    return grouped;
  }, [filteredFixtures]);

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: '#0f172a', zIndex: 9999, overflowY: 'auto',
      display: 'flex', flexDirection: 'column'
    }}>
      <div style={{ padding: '20px', background: 'rgba(30, 41, 59, 0.95)', borderBottom: '1px solid #1e293b', position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', width: '48px', height: '48px', borderRadius: '50%', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }} title="Voltar">
            <ArrowLeft size={24} />
          </button>
          <h1 style={{ fontSize: '24px', color: '#fff', margin: 0 }}>
            {selectedYear} {tournament.name} Resultados
          </h1>
        </div>


        <p style={{ color: '#94a3b8', margin: '0 0 20px 0', fontSize: '14px' }}>
          Torneio oficial do circuito
        </p>

        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative' }}>
            <button 
              onClick={() => { setIsCategoryDropdownOpen(!isCategoryDropdownOpen); setIsYearDropdownOpen(false); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                background: 'transparent', border: '1px solid #cbd5e1', color: '#cbd5e1',
                padding: '8px 16px', borderRadius: '20px', fontSize: '14px', cursor: 'pointer'
              }}
            >
              {selectedCategory || 'Categoria'} <ChevronDown size={16} color={isCategoryDropdownOpen ? '#3b82f6' : '#cbd5e1'} />
            </button>
            {isCategoryDropdownOpen && (
              <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: '8px', background: '#fff', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', minWidth: '200px', zIndex: 20 }}>
                {availableCategories.map(cat => (
                  <div 
                    key={cat}
                    onClick={() => { setSelectedCategory(cat); setIsCategoryDropdownOpen(false); }}
                    style={{ padding: '12px 16px', color: '#000', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', fontSize: '14px' }}
                  >
                    <span>{cat}</span>
                    {selectedCategory === cat && <Check size={16} color="#3b82f6" />}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ position: 'relative' }}>
            <button 
              onClick={() => { setIsYearDropdownOpen(!isYearDropdownOpen); setIsCategoryDropdownOpen(false); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                background: 'transparent', border: '1px solid #cbd5e1', color: '#cbd5e1',
                padding: '8px 16px', borderRadius: '20px', fontSize: '14px', cursor: 'pointer'
              }}
            >
              {selectedYear || 'Ano'} <ChevronDown size={16} color={isYearDropdownOpen ? '#3b82f6' : '#cbd5e1'} />
            </button>
            {isYearDropdownOpen && (
              <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: '8px', background: '#fff', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', minWidth: '150px', zIndex: 20, maxHeight: '300px', overflowY: 'auto' }}>
                {availableYears.map(year => (
                  <div 
                    key={year}
                    onClick={() => { setSelectedYear(year); setIsYearDropdownOpen(false); }}
                    style={{ padding: '12px 16px', color: '#000', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', fontSize: '14px' }}
                  >
                    <span>{year}</span>
                    {selectedYear === year && <Check size={16} color="#3b82f6" />}
                  </div>
                ))}
              </div>
            )}
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#cbd5e1', fontSize: '13px', marginLeft: 'auto' }}>
            <input 
              type="checkbox" 
              checked={isLiveFilter} 
              onChange={e => setIsLiveFilter(e.target.checked)} 
              style={{ accentColor: '#ef4444' }}
            />
            <span><span style={{ color: '#ef4444' }}>🔴</span> Somente Ao Vivo</span>
          </label>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: '20px' }}><ShimmerLoading /></div>
      ) : (
        <div style={{ padding: '0 20px 40px', maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
          
          <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '12px', marginTop: '20px', padding: '10px' }}>
            <DateCarousel 
              selectedDate={selectedDate} 
              onSelectDate={setSelectedDate} 
            />
          </div>

          <h2 style={{ fontSize: '18px', color: '#fff', margin: '24px 0 16px' }}>{selectedCategory}</h2>

          <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '20px' }}>
            {Object.keys(groupedFixtures).length === 0 ? (
              <p style={{ color: '#64748b', textAlign: 'center', padding: '40px 0' }}>
                Nenhum jogo encontrado para esta data.
              </p>
            ) : (
              Object.keys(groupedFixtures).map(round => (
                <div key={round} style={{ marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#334155', margin: '0 0 12px 0', paddingBottom: '8px', borderBottom: '1px solid #e2e8f0', textTransform: 'uppercase' }}>
                    {round}
                  </h3>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '16px' }}>
                    {groupedFixtures[round].map(fixture => (
                      <div key={fixture.id} style={{ border: '1px solid #e2e8f0', borderRadius: '8px', background: '#fff' }}>
                         <MatchTile fixture={fixture} />
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
